import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Drawer,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { AppColors } from '../../../../util/AppColors';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSpeciesList,
  createSpecies,
  updateSpecies,
  deleteSpecies
} from '../../../../redux/reducers/speciesSlice';
import CustomModal from '../../../../components/CustomModal/CustomModal';
import CustomButton from '../../../../components/CustomButton';

function Species({ onSpeciesClick }) {
  const dispatch = useDispatch();

  const [search, setSearch] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ speciesName: '' });
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  const speciesState = useSelector((state) => state.species);
  const speciesList = speciesState.list;

  useEffect(() => {
    dispatch(getSpeciesList());
  }, [dispatch]);

  const handleAddOrUpdate = () => {
    if (!form.speciesName.trim()) return;

    if (editIndex !== null) {
      const species = speciesList[editIndex];
      dispatch(updateSpecies({ speciesId: species.speciesId, speciesName: form.speciesName }));
    } else {
      dispatch(createSpecies(form));
    }

    handleCloseDrawer();
  };

  const handleEdit = (index) => {
    const species = speciesList[index];
    setForm({ speciesName: species.speciesName });
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleOpenDelModal = (index) => {
    const species = speciesList[index];
    const breedsLength = species.breeds?.length || 0; // Default to 0 if breeds is undefined/null

    if (breedsLength > 0) {
      setShowWarning(true); // Show warning modal
      setDelModVisible(true);
      setDeleteIndex(index);
    } else {
      setShowWarning(false); // Show confirm modal
      setDelModVisible(true);
      setDeleteIndex(index);
    }
  };

  const handleDelConfirm = () => {
    const species = speciesList[deleteIndex];
    if (species) {
      dispatch(deleteSpecies({ speciesId: species.speciesId }));
    }
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setForm({ speciesName: '' });
    setEditIndex(null);
  };

  const filteredRows = speciesList
    .filter((row) => row.speciesName.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));

  return (
    <Box p={3}>
      {/* Top section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap">
        <Box display="flex" gap={2} mx="auto" mb={2}>
          <TextField
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ width: '500px', backgroundColor: AppColors.lightPink }}
          />
          <Button variant="contained" onClick={() => setOpenDrawer(true)}>
            Create
          </Button>
        </Box>
        <Typography variant="h6" sx={{ ml: 'auto' }}>
          Total Species: {filteredRows.length}
        </Typography>
      </Box>

      {/* Table */}
      <Box sx={{ mt: 2, height: '60vh', overflowY: 'auto' }}>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Species Name</TableCell>
                <TableCell>No of Breeds</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No records found</TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={row.speciesId}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography
                        color="primary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => onSpeciesClick?.(row.speciesId)}
                      >
                        {row.speciesName}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.breeds?.length || 0}</TableCell>
                    <TableCell>{row.userName || '-'}</TableCell>
                    <TableCell>{new Date(row.createdDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small" onClick={() => handleEdit(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleOpenDelModal(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Drawer for Create & Edit */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={300}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="primary">
              {editIndex !== null ? 'Edit Species' : 'Create Species'}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Species Name"
            value={form.speciesName}
            onChange={(e) => setForm({ ...form, speciesName: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: 'absolute',
                top: '50%',
                left: '14px',
                transform: 'translateY(-50%)',
                transition: 'all 0.2s ease-out',
                '&.Mui-focused, &.MuiFormLabel-filled': {
                  top: '0px',
                  transform: 'translateY(-50%) scale(0.75)',
                  transformOrigin: 'top left',
                }
              }
            }}
          />
          <Button fullWidth variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleAddOrUpdate}>
            {editIndex !== null ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Drawer>

      {/* Confirmation Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header={showWarning ? "Cannot Delete" : "Delete"}
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          {showWarning
            ? "This record cannot be deleted as there are breeds associated with it."
            : "Are you sure you want to delete this record?"}
        </Typography>

        {/* Show buttons only if it's a confirmation modal */}
        {!showWarning && (
          <div className="flex-center mb10 mt15">
            <div>
              <CustomButton text="Cancel" onClick={handleDelModClose} />
            </div>
            <div className="ml10">
              <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
            </div>
          </div>
        )}
      </CustomModal>
    </Box>
  );
}

export default Species;